import * as React from "react";
import { useCallback, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { createWorkspace } from "../../../services/workspacesApi";
import { toastWorkspaceApiError } from "../../../utils/workspaceApiErrors";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type { WorkspaceDetail } from "../../../types/workspace";

const MAX_NAME_LENGTH = 120;

export type WorkspaceCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: WorkspaceAccountScope;
  onCreated: (workspace: WorkspaceDetail) => void;
};

export function WorkspaceCreateDialog({
  open,
  onOpenChange,
  scope,
  onCreated,
}: WorkspaceCreateDialogProps): React.ReactElement {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const reset = useCallback(() => {
    setName("");
    setCreating(false);
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange(next);
    },
    [onOpenChange, reset]
  );

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.warning("Name required", { description: "Enter a workspace name." });
      return;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      toast.warning("Name too long", {
        description: `Workspace names must be ${MAX_NAME_LENGTH} characters or fewer.`,
      });
      return;
    }

    setCreating(true);
    try {
      const workspace = await createWorkspace(scope, { name: trimmed });
      toast.success("Workspace created");
      handleOpenChange(false);
      onCreated(workspace);
    } catch (err) {
      toastWorkspaceApiError(err, "Create failed");
    } finally {
      setCreating(false);
    }
  }, [handleOpenChange, name, onCreated, scope]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-workspace-create-dialog=""
        className="sm:max-w-md font-[family-name:var(--font-primary)]"
      >
        <DialogHeader>
          <DialogTitle className="text-[color:var(--brand-tropical-surf)]">
            New workspace
          </DialogTitle>
          <DialogDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
            Create a workspace for the active account. You can add strategies, reports, and
            simulations on the canvas.
          </DialogDescription>
        </DialogHeader>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="workspace-name" className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
              Name
            </FieldLabel>
            <Input
              id="workspace-name"
              data-workspace-create-name=""
              value={name}
              maxLength={MAX_NAME_LENGTH}
              placeholder="e.g. Q3 momentum review"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !creating) void handleCreate();
              }}
              autoFocus
            />
          </Field>
        </FieldGroup>
        <DialogFooter>
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            onClick={() => handleOpenChange(false)}
            disabled={creating}
          >
            <span className="normal-case tracking-normal">Cancel</span>
          </Button>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            onClick={() => void handleCreate()}
            disabled={creating || !name.trim()}
          >
            {creating ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span className="normal-case tracking-normal">Create workspace</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
